
import Page from "@/components/Page.vue";
import AppHeader from "@/components/AppHeader.vue";
import useUser from "@/composables/useUser";
import { useGetUserMyProfileQuery, UserType } from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import useError from "@/composables/useError";
import { VDError } from "@/models/VDError";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { computed, reactive, toRef, watch } from "vue";
import useUpdateUser from "./User/mutations/useUpdateUser";
import Error from "@/components/Error.vue";
import useToastService from "@/composables/useToastService";
import gql from "graphql-tag";

// Queries
gql`
  query getUserMyProfile($id: ID) {
    getUserMyProfile(userId: $id) {
      ...UserParts
      roles {
        roleId
        organizationId
        applicationId
        roleName
        organizationName
        applicationName
      }
    }
  }
`;

export default {
  components: { Page, AppHeader, Error },
  setup() {
    const { user: profile } = useUser();
    const toastService = useToastService();
    const { result, loading, error } = useGetUserMyProfileQuery(
      () => ({
        id: profile.value?.userId ?? "",
      }),
      () => ({
        enabled: !!profile.value?.userId, // Only run query if userId exists
        errorPolicy: "all", // Still return partial data if there are errors
      })
    );

    const user = reactive<UserType>({ roles: [] });

    // Simple static error message
    const staticError = computed((): VDError | undefined => {
      const apolloError = useError(error);
      if (apolloError.value) {
        return {
          title: "Der opstod en fejl",
          message:
            "Vi kunne ikke hente din profil på nuværende tidspunkt. Prøv at genindlæse siden eller kontakt support hvis problemet fortsætter.",
        };
      }
      return undefined;
    });

    const v$ = useVuelidate(useValidations(), {
      firstname: toRef(user, "firstname"),
      lastname: toRef(user, "lastname"),
    });

    const updateUserMutation = useUpdateUser(
      profile.value?.organizationId,
      profile.value?.userId ?? "",
      user,
      false
    );

    // Computed role lists
    const userRoles = computed(() => {
      return (
        user.roles?.filter(
          (role) => role?.organizationId === user.organization?.organizationId
        ) ?? []
      );
    });

    const onBehalfOfRoles = computed(() => {
      return (
        user.roles?.filter(
          (role) => role?.organizationId !== user.organization?.organizationId
        ) ?? []
      );
    });

    watch(
      () => useResult(result).value,
      (newValue) => {
        if (newValue) {
          Object.assign(user, newValue);
        }
      },
      { immediate: true }
    );

    // Watch for user profile availability
    watch(
      () => profile.value?.userId,
      (userId) => {
        if (!userId) {
          console.warn("Me.vue: No valid userId found in user profile");
        }
      },
      { immediate: true }
    );

    updateUserMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Gemt",
        message: `Dine detajler er blevet opdateret`,
      });
    });

    updateUserMutation.onError((error) => {
      console.error("Me.vue: Error updating user profile:", error);
      const apolloError = useError(error);
      toastService.show({
        severity: "error",
        error: apolloError.value || {
          title: "Fejl ved opdatering",
          message:
            "Der opstod en fejl ved opdatering af din profil. Prøv igen senere.",
        },
      });
    });

    return {
      user,
      userRoles,
      onBehalfOfRoles,
      error: useError(error),
      staticError,
      loading,
      v$,
      save: () => updateUserMutation.call(),
      actionButtonLoading: computed(() => updateUserMutation.loading.value),
    };
  },
};

function useValidations() {
  return {
    firstname: { required },
    lastname: { required },
  };
}
